import { getConfig } from "../config";
import { getIdToken } from "./auth";

export interface ThreadMessage {
  id: string;
  thread_id: string;
  sender_cognito_sub: string;
  sender_role: "coach" | "guardian" | "athlete";
  body: string;
  created_at: string;
}

/**
 * Opens the app's WebSocket connection (architecture §2b). The ID token travels as a `?token=` query
 * string param, not an Authorization header -- the browser's native WebSocket API can't set custom
 * headers, which is exactly why lambda/websocket/authorizer.ts reads it from the query string instead.
 */
export async function connectMessagingSocket(onMessage: (message: ThreadMessage) => void): Promise<WebSocket> {
  const token = await getIdToken();
  const ws = new WebSocket(`${getConfig().webSocketUrl}?token=${encodeURIComponent(token ?? "")}`);
  ws.addEventListener("message", (event) => {
    try {
      onMessage(JSON.parse(event.data as string) as ThreadMessage);
    } catch {
      // A frame that isn't the JSON we expect must not throw inside the listener. It would surface as an
      // unhandled error rather than anything actionable, and one malformed frame should not be able to
      // take the connection's message handling down with it.
      console.warn("Ignoring an unparseable WebSocket frame");
    }
  });
  return ws;
}

/**
 * `action: "sendMessage"` is API Gateway WebSocket's own route-selection field (default
 * `$request.body.action`) -- it's how the API routes this frame to lambda/websocket/send-message.ts
 * rather than some other route, not something the Lambda itself reads out of the body.
 */
export function sendThreadMessage(ws: WebSocket, threadId: string, body: string): void {
  ws.send(JSON.stringify({ action: "sendMessage", threadId, body }));
}
